/**
 * Services wrapper to support:
 *   - switching between same service provides (for example news from Gateway vs directly from News service)
 *   - initialization of public services and authenticated services
 *   - connecting and disconnecting groups of services
 * */
import { Dispatch, PayloadAction } from '@reduxjs/toolkit';

import configLib from '../../configLib';
import { GWQueryCase, GWRequestType } from '../models/gateway/types';
import { Order } from '../models/trading/types';
import { isGatewayMDFullyEnabled } from '../store/selectors';
import { ModifyOrCancelOrderRequest } from '../store/trading/types';
import OrderInfoCache from '../store-util/OrderInfoCache';
import { setSOROrderData } from '../util/TradingHelpers';

import { GatewayWebSocketService } from './web-socket/GatewayWebSocket.service';
import GatewayRestService from './GatewayRest.service';
import GatewayWSService from './GatewayWS.service';
import services from './index';
import {
  IAMSService,
  IAuthService,
  ICommonAuthService,
  ICRMService,
  IFileService,
  IFundamentalService,
  IMarketDataService,
  INewsService,
  IPaymentService,
  IReportingFileService,
  IReportingService,
  ITradingService,
} from './types';

const { sorApiEnabled: SOR_ENABLED } = configLib;

class GlobalService {
  dispatch: (action: PayloadAction<any>) => void;

  public static gatewayWs: GatewayWSService;

  public static gatewayRest: GatewayRestService;

  public static globalService: GlobalService;

  public static marketDataService: IMarketDataService;

  public static newsService: INewsService;

  public static fileService: IFileService;

  // TODO: Next ones
  public static authService: IAuthService & ICommonAuthService;

  public static crmService: ICRMService;

  public static fundamentalService: IFundamentalService;

  public static reportingService: IReportingService;

  public static reportingFileService: IReportingFileService;

  public static tradeService: ITradingService;

  public static paymentService: IPaymentService;

  public static amsService: IAMSService;

  public static createPublicServices() {
    GlobalService.authService = services.createAuthService();
    GlobalService.crmService = services.createCRMService();
  }

  // TODO: ART-3959 this logic will be refactored
  public static createServices(dispatch: Dispatch) {
    GlobalService.globalService = new GlobalService(dispatch);
    // WebSocket
    GlobalService.gatewayWs = services.createGatewayWSService(dispatch);
    GlobalService.tradeService = services.createTradingService(dispatch);
    // REST
    GlobalService.gatewayRest = services.createGatewayRestService();
    GlobalService.crmService = services.createCRMService();
    GlobalService.newsService = services.createNewsService();
    GlobalService.fileService = services.createFileService();
    GlobalService.fundamentalService = services.createFundamentalService();
    GlobalService.reportingService = services.createReportingService();
    GlobalService.reportingFileService = services.createReportingFileService();
    GlobalService.paymentService = services.createPaymentService();
    GlobalService.amsService = services.createAmsService();
    // to be deprecated
    if (!isGatewayMDFullyEnabled()) {
      GlobalService.marketDataService = services.createMarketDataService(dispatch);
    }
  }

  /**
   * Connects Web Socket services - Gateway and TAPI
   * (Direct WS connection to MDS is not used any more but is still not removed from code and can be easily used for debugging for example)
   */
  public static connect(isReconnect = false) {
    const gatewayWebSocket = GatewayWebSocketService.getInstance();
    const isGatewayActive = !!gatewayWebSocket?.isConnected() || !!gatewayWebSocket?.isBusy();
    const isTradeServiceConnected = GlobalService.tradeService?.isConnected();
    if (!isGatewayActive) {
      gatewayWebSocket.connect();
    }
    if (!isTradeServiceConnected) {
      GlobalService.tradeService.connect();
    }
    if (!isGatewayMDFullyEnabled()) {
      GlobalService.marketDataService.connect();
    }
  }

  /**
   * Disonnects Web Socket services - Gateway and TAPI
   * (Direct WS connection to MDS is not used any more but is still not removed from code and can be easily used for debugging for example)
   */
  public static disconnect() {
    const gatewayWebSocket = GatewayWebSocketService.getInstance();
    const isGatewayConnected = !!gatewayWebSocket?.isConnected();
    const isTradeServiceConnected = GlobalService.tradeService?.isConnected();
    if (isTradeServiceConnected) {
      GlobalService.tradeService?.disconnect();
    }
    if (isGatewayConnected) {
      gatewayWebSocket?.disconnect();
    }
    if (!isGatewayMDFullyEnabled()) {
      GlobalService.marketDataService?.disconnect();
    }
  }

  constructor(dispatch: Dispatch) {
    this.dispatch = dispatch;
  }

  order(action: 'neworder' | 'modifyOrder' | 'cancel', data: Order | ModifyOrCancelOrderRequest) {
    let isGWCall = (SOR_ENABLED as any)[action];
    let messageType: GWRequestType | null = null;

    if (isGWCall) {
      let orderData = {};
      messageType = (data as Order)?.requestType ?? (data as ModifyOrCancelOrderRequest)?.order?.requestType ?? null;

      switch (messageType) {
        case GWRequestType.NewOrderRequest:
          orderData = Object.assign(setSOROrderData(data), {
            capacity: 'Agency',
            status: 'New',
            messageType,
            dateTime: new Date().toISOString(),
          });
          break;
        case GWRequestType.ModifyOrderRequest:
          orderData = {
            ...setSOROrderData(data),
            origClOrdId: data.originalClientOrderId,
            messageType,
          };
          break;
        case GWRequestType.CancelOrderRequest: {
          const { clientOrderId, originalClientOrderId, ...rest } = data as ModifyOrCancelOrderRequest & Order;
          orderData = {
            ...rest,
            clOrdId: clientOrderId,
            origClOrdId: originalClientOrderId,
          };
          break;
        }
        default:
          console.error(`[Global.service] order - unknown order request - ${messageType}`);
          break;
      }
      OrderInfoCache.push(orderData); // this is just for debug
      GlobalService.gatewayWs.sendRequest(messageType!, orderData, GWQueryCase.Order);
    } else {
      const { order, account } = (data as ModifyOrCancelOrderRequest) ?? {};
      // TODO: Implement this else if needed - the goal is to use SOR only.
      //       In this else case - direct OMS connection through Trading API is used.
      //       Since it is not intended to be supported, it is recommended to use tapi-public instead in case
      //       Trading API connection is needed.
      switch (action) {
        case 'neworder':
          GlobalService.tradeService.newOrder(data);
          break;
        case 'modifyOrder': {
          GlobalService.tradeService.modifyOrder(data ?? order, account);
          break;
        }
        case 'cancel': {
          GlobalService.tradeService.cancelOrder(order, account);
          break;
        }
        default:
          break;
      }
    }
  }
}

export default GlobalService;
